import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LOGO_URL = "https://public.readdy.ai/ai/img_res/f7c36657-f3e9-41c7-b237-9b499e377f01.png";

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch("https://readdy.ai/api/form/d7157t4991ove783sot0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#F0EDE8] pt-16 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8 pb-12 border-b border-[#D9D9D9]/60">
          {/* Brand */}
          <div>
            <img src={LOGO_URL} alt="TaBiFoTo" className="h-8 w-auto object-contain mb-4" />
            <p className="text-sm text-[#2C2C2C]/50 leading-relaxed max-w-xs">
              {t("tabifoto.footer.tagline")}
            </p>
          </div>

          {/* Nav links */}
          <div className="flex gap-12">
            <div>
              <p className="text-xs uppercase tracking-widest text-[#C8B8A6] mb-4">
                {t("tabifoto.footer.links_explore")}
              </p>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-sm text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap">
                    {t("tabifoto.footer.link_home")}
                  </Link>
                </li>
                <li>
                  <Link to="/booking" className="text-sm text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap">
                    {t("tabifoto.footer.link_booking")}
                  </Link>
                </li>
                <li>
                  <Link to="/philosophy" className="text-sm text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap">
                    {t("tabifoto.footer.link_philosophy")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-[#C8B8A6] mb-4">
                {t("tabifoto.footer.links_info")}
              </p>
              <ul className="space-y-3">
                <li>
                  <a href="#faq" className="text-sm text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap" rel="nofollow">
                    {t("tabifoto.footer.link_faq")}
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-sm text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap" rel="nofollow">
                    {t("tabifoto.footer.link_contact")}
                  </a>
                </li>
                <li>
                  <a href="#terms" className="text-sm text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap" rel="nofollow">
                    {t("tabifoto.footer.link_terms")}
                  </a>
                </li>
                <li>
                  <Link to="/photographer/apply" className="text-sm text-[#C8B8A6] hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap">
                    {t("tabifoto.footer.link_apply")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C8B8A6] mb-4">
              {t("tabifoto.footer.newsletter_label")}
            </p>
            {submitted ? (
              <p className="text-sm text-[#2C2C2C]/60">Thank you for subscribing.</p>
            ) : (
              <form
                data-readdy-form
                id="newsletter-footer"
                onSubmit={handleSubmit}
                className="flex flex-col gap-3"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("tabifoto.footer.newsletter_placeholder")}
                  className="bg-transparent border-b border-[#C8B8A6] pb-2 text-sm text-[#2C2C2C] placeholder-[#2C2C2C]/30 focus:outline-none focus:border-[#2C2C2C] transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="self-start px-5 py-2 rounded-full border border-[#2C2C2C] text-xs tracking-widest uppercase text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-[#F7F6F3] transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {t("tabifoto.footer.newsletter_submit")}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="pt-8 flex items-center justify-between">
          <p className="text-xs text-[#2C2C2C]/30 tracking-wider">
            {t("tabifoto.footer.copyright")}
          </p>
          <Link
            to="/admin"
            className="text-xs text-[#2C2C2C]/20 hover:text-[#2C2C2C]/50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
